import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { buildAttorneyReviewItems } from '../../lib/attorneyReviewRules';
import ProfessionalLearningInsights from '../../components/education/ProfessionalLearningInsights';
import ProfessionalAssignLearning from '../../components/education/ProfessionalAssignLearning';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const formatSupabaseError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const statusOptions = [
  { value: 'not_reviewed', label: 'Not Reviewed' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'needs_discussion', label: 'Needs Discussion' },
  { value: 'waiting_for_information', label: 'Waiting for Information' },
  { value: 'decision_made', label: 'Decision Made' },
  { value: 'not_applicable', label: 'Not Applicable' }
];

const statusStyles = {
  not_reviewed: 'bg-slate-50 text-slate-600 border-slate-200',
  reviewed: 'bg-green-50 text-green-700 border-green-100',
  needs_discussion: 'bg-amber-50 text-amber-700 border-amber-100',
  waiting_for_information: 'bg-blue-50 text-blue-700 border-blue-100',
  decision_made: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  not_applicable: 'bg-slate-100 text-slate-500 border-slate-200'
};

const warningStyles = {
  info: 'text-slate-400',
  warning: 'text-amber-500',
  critical: 'text-red-500'
};

const getStatusLabel = (status) => {
  return statusOptions.find((option) => option.value === status)?.label || status?.replaceAll('_', ' ');
};

const PERSON_LIST_OVERRIDE_FIELDS = new Set(['children', 'residue_named_beneficiaries']);
const TEXT_LIST_OVERRIDE_FIELDS = new Set([
  'specific_gifts',
  'debt_forgiveness',
  'patient_advocate_additional',
  'financial_power_of_attorney_additional',
  'hipaa_authorized_individuals'
]);

const NAME_OVERRIDE_GROUPS = [
  {
    key: 'testator',
    label: 'Testator Name',
    firstField: 'testator_first_name',
    middleField: 'testator_middle_initial',
    lastField: 'testator_last_name',
    fullNameField: 'testator_name'
  },
  {
    key: 'spouse',
    label: 'Spouse Name',
    firstField: 'spouse_first_name',
    middleField: 'spouse_middle_initial',
    lastField: 'spouse_last_name',
    fullNameField: 'spouse_name'
  }
];

const cleanText = (value) => String(value || '').trim();

const normalizeMiddleInitial = (value) => cleanText(value).charAt(0).toUpperCase();

const composeName = (person) => [
  cleanText(person.first_name),
  normalizeMiddleInitial(person.middle_initial),
  cleanText(person.last_name)
].filter(Boolean).join(' ');

const splitName = (value) => {
  const parts = cleanText(value).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', middle_initial: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], middle_initial: '', last_name: '' };
  if (parts.length === 2) return { first_name: parts[0], middle_initial: '', last_name: parts[1] };
  return {
    first_name: parts[0],
    middle_initial: normalizeMiddleInitial(parts[1]),
    last_name: parts.slice(2).join(' ')
  };
};

const stringifyValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not provided';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Not provided';
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return Object.values(item).filter(Boolean).join(' - ');
        }
        return String(item);
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const normalizePersonDraftRows = (value) => {
  const rows = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/\r?\n/).map((description) => ({ description })).filter((row) => row.description.trim())
      : [];
  const normalized = rows.map((row) => {
    if (!row || typeof row !== 'object') {
      return {
        first_name: '',
        middle_initial: '',
        last_name: '',
        date_of_birth: '',
        relationship: 'child'
      };
    }

    return {
      ...splitName(row.full_name || row.name || row.recipient || row.description),
      first_name: cleanText(row.first_name || row.firstName) || splitName(row.full_name || row.name || row.recipient || row.description).first_name,
      middle_initial: normalizeMiddleInitial(row.middle_initial || row.middleInitial) || splitName(row.full_name || row.name || row.recipient || row.description).middle_initial,
      last_name: cleanText(row.last_name || row.lastName) || splitName(row.full_name || row.name || row.recipient || row.description).last_name,
      date_of_birth: cleanText(row.date_of_birth || row.dateOfBirth),
      relationship: cleanText(row.relationship),
      share: cleanText(row.share || row.percentage || row.amount)
    };
  });

  return normalized.length > 0 ? normalized : [{
    first_name: '',
    middle_initial: '',
    last_name: '',
    date_of_birth: '',
    relationship: 'child'
  }];
};

const personRowsToApprovedValue = (fieldKey, value) => {
  return normalizePersonDraftRows(value)
    .map((person) => {
      const fullName = composeName(person);
      if (!fullName && !person.date_of_birth && !person.relationship && !person.share) return null;
      const defaultRelationship = fieldKey === 'children' ? 'child' : '';
      return {
        first_name: cleanText(person.first_name),
        middle_initial: normalizeMiddleInitial(person.middle_initial),
        last_name: cleanText(person.last_name),
        full_name: fullName,
        date_of_birth: person.date_of_birth || null,
        relationship: cleanText(person.relationship) || defaultRelationship || null,
        share: cleanText(person.share) || null
      };
    })
    .filter(Boolean);
};

const textListDraftFor = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.description || item.name || item.recipient || item.full_name || Object.values(item).filter(Boolean).join(' - ');
        }
        return String(item || '');
      })
      .filter(Boolean)
      .join('\n');
  }
  return typeof value === 'string' ? value : '';
};

const textListToApprovedValue = (value) => {
  return cleanText(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((description) => ({ description }));
};

const editableValueFor = (fieldKey, value) => {
  if (PERSON_LIST_OVERRIDE_FIELDS.has(fieldKey)) return normalizePersonDraftRows(value);
  if (TEXT_LIST_OVERRIDE_FIELDS.has(fieldKey)) return textListDraftFor(value);
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const parseEditableValue = (fieldKey, draftValue, sourceValue) => {
  if (PERSON_LIST_OVERRIDE_FIELDS.has(fieldKey)) return personRowsToApprovedValue(fieldKey, draftValue);
  if (TEXT_LIST_OVERRIDE_FIELDS.has(fieldKey)) return textListToApprovedValue(draftValue);

  const valueText = String(draftValue ?? '');
  const trimmed = valueText.trim();
  if (trimmed === '') return null;

  if (Array.isArray(sourceValue) || (sourceValue && typeof sourceValue === 'object')) {
    return JSON.parse(trimmed);
  }

  if (typeof sourceValue === 'boolean') {
    if (/^(true|yes)$/i.test(trimmed)) return true;
    if (/^(false|no)$/i.test(trimmed)) return false;
  }

  if (typeof sourceValue === 'number') {
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return trimmed;
};

const valueTypeFor = (value) => {
  if (Array.isArray(value)) return 'array';
  if (value === null || value === undefined) return 'unknown';
  if (typeof value === 'string') return 'text';
  return typeof value;
};

const jsonbValueForSave = (value) => {
  if (value === undefined || value === null) return '';
  return value;
};

const getOriginalFieldValue = (item, fieldKey) => {
  const answer = item.original_answer_json?.[fieldKey];
  return answer?.decision ?? answer?.intake ?? null;
};

const getCurrentOverrideValue = (item, overridesByField, fieldKey) => {
  return overridesByField[fieldKey]?.approved_value_json ?? getOriginalFieldValue(item, fieldKey);
};

const nameDraftFor = (item, overridesByField, group) => ({
  ...splitName(getCurrentOverrideValue(item, overridesByField, group.fullNameField)),
  first_name: cleanText(getCurrentOverrideValue(item, overridesByField, group.firstField)) || splitName(getCurrentOverrideValue(item, overridesByField, group.fullNameField)).first_name,
  middle_initial: normalizeMiddleInitial(getCurrentOverrideValue(item, overridesByField, group.middleField)) || splitName(getCurrentOverrideValue(item, overridesByField, group.fullNameField)).middle_initial,
  last_name: cleanText(getCurrentOverrideValue(item, overridesByField, group.lastField)) || splitName(getCurrentOverrideValue(item, overridesByField, group.fullNameField)).last_name
});

const NameOverrideInput = ({ label, value, onChange }) => {
  const nameValue = value || { first_name: '', middle_initial: '', last_name: '' };

  const updateName = (key, nextValue) => {
    onChange({ ...nameValue, [key]: nextValue });
  };

  return (
    <div>
      <p className="text-xs font-bold text-slate-700 mb-2">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr] gap-3">
        <label className="text-xs font-semibold text-slate-600">
          First Name
          <input
            type="text"
            value={nameValue.first_name || ''}
            onChange={(event) => updateName('first_name', event.target.value)}
            className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Middle Initial
          <input
            type="text"
            maxLength={1}
            value={nameValue.middle_initial || ''}
            onChange={(event) => updateName('middle_initial', event.target.value)}
            className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Last Name
          <input
            type="text"
            value={nameValue.last_name || ''}
            onChange={(event) => updateName('last_name', event.target.value)}
            className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          />
        </label>
      </div>
    </div>
  );
};

const PersonListOverrideInput = ({ fieldKey, rows, onChange }) => {
  const people = normalizePersonDraftRows(rows);
  const isBeneficiaryList = fieldKey === 'residue_named_beneficiaries';
  const itemLabel = isBeneficiaryList ? 'Beneficiary' : 'Child';

  const updatePerson = (index, key, value) => {
    onChange(people.map((person, personIndex) => (
      personIndex === index ? { ...person, [key]: value } : person
    )));
  };

  const addPerson = () => {
    onChange([
      ...people,
      {
        first_name: '',
        middle_initial: '',
        last_name: '',
        date_of_birth: '',
        relationship: 'child'
      }
    ]);
  };

  const removePerson = (index) => {
    const nextRows = people.filter((_, personIndex) => personIndex !== index);
    onChange(nextRows.length > 0 ? nextRows : normalizePersonDraftRows([]));
  };

  return (
    <div className="space-y-3">
      {people.map((person, index) => (
        <div key={index} className="rounded-md border border-emerald-100 bg-emerald-50/40 p-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_160px_44px] gap-3">
            <label className="text-xs font-semibold text-slate-600">
              First Name
              <input
                type="text"
                value={person.first_name || ''}
                onChange={(event) => updatePerson(index, 'first_name', event.target.value)}
                className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Middle Initial
              <input
                type="text"
                maxLength={1}
                value={person.middle_initial || ''}
                onChange={(event) => updatePerson(index, 'middle_initial', event.target.value)}
                className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Last Name
              <input
                type="text"
                value={person.last_name || ''}
                onChange={(event) => updatePerson(index, 'last_name', event.target.value)}
                className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              {isBeneficiaryList ? 'Share or Relationship' : 'Date of Birth'}
              {isBeneficiaryList ? (
                <input
                  type="text"
                  value={person.share || person.relationship || ''}
                  onChange={(event) => updatePerson(index, 'share', event.target.value)}
                  className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                />
              ) : (
                <input
                  type="date"
                  value={person.date_of_birth || ''}
                  onChange={(event) => updatePerson(index, 'date_of_birth', event.target.value)}
                  className="mt-1 block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                />
              )}
            </label>
            <button
              type="button"
              onClick={() => removePerson(index)}
              className="mt-5 inline-flex h-10 w-10 items-center justify-center rounded-md border border-emerald-100 bg-white text-emerald-700 hover:bg-emerald-50"
              aria-label={`Remove ${itemLabel.toLowerCase()}`}
            >
              <SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addPerson}
        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-md text-emerald-700 bg-white border border-emerald-100 hover:bg-emerald-50"
      >
        <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-3.5 w-3.5" />
        Add {itemLabel}
      </button>
    </div>
  );
};

const AnswerPreview = ({ answers }) => {
  const entries = Object.entries(answers || {});

  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">No related intake values captured for this item.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {entries.map(([fieldKey, value]) => {
        const displayValue = value?.decision ?? value?.intake;
        return (
          <div key={fieldKey} className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{fieldKey}</p>
            <p className="text-sm text-slate-700 whitespace-pre-line">{stringifyValue(displayValue)}</p>
          </div>
        );
      })}
    </div>
  );
};

const ApprovedOverrideEditor = ({
  item,
  overridesByField,
  overrideDrafts,
  savingOverrideKey,
  onDraftChange,
  onUseCurrentValue,
  onUseNameCurrentValue,
  onSaveNameOverride,
  onSaveOverride
}) => {
  const fieldKeys = Array.isArray(item.related_field_keys) ? item.related_field_keys : Object.keys(item.original_answer_json || {});
  const nameGroups = NAME_OVERRIDE_GROUPS.filter((group) => (
    fieldKeys.includes(group.firstField) || fieldKeys.includes(group.middleField) || fieldKeys.includes(group.lastField)
  ));
  const groupedNameFields = new Set(nameGroups.flatMap((group) => [
    group.firstField,
    group.middleField,
    group.lastField,
    group.fullNameField
  ]));
  const visibleFieldKeys = fieldKeys.filter((fieldKey) => !groupedNameFields.has(fieldKey));

  if (fieldKeys.length === 0) return null;

  return (
    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
        <div>
          <h4 className="text-sm font-bold text-emerald-950 flex items-center">
            <SafeIcon icon={FiIcons.FiEdit3} className="mr-2 h-4 w-4 text-emerald-700" />
            Attorney-Approved Values
          </h4>
          <p className="text-xs text-emerald-700 mt-1">
            Approved values are used first during document generation.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {nameGroups.map((group) => {
          const draftKey = `${item.id}:${group.key}_name_parts`;
          const draftValue = overrideDrafts[draftKey] ?? nameDraftFor(item, overridesByField, group);
          const approvedAt = [
            overridesByField[group.firstField]?.approved_at,
            overridesByField[group.middleField]?.approved_at,
            overridesByField[group.lastField]?.approved_at
          ].filter(Boolean).sort().at(-1);

          return (
            <div key={group.key} className="bg-white rounded-md border border-emerald-100 p-3">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{group.label}</p>
                  <p className="mt-1 text-xs text-slate-500 whitespace-pre-line">
                    Current: {composeName(nameDraftFor(item, {}, group)) || 'Not provided'}
                  </p>
                  {approvedAt && (
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                      Approved {format(new Date(approvedAt), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onUseNameCurrentValue(item, group)}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-md text-emerald-700 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100"
                >
                  Use Current
                </button>
              </div>
              <div className="mt-3 flex flex-col gap-3">
                <NameOverrideInput
                  label="Approved Name"
                  value={draftValue}
                  onChange={(nextValue) => onDraftChange(draftKey, nextValue)}
                />
                <button
                  type="button"
                  onClick={() => onSaveNameOverride(item, group)}
                  disabled={savingOverrideKey === draftKey}
                  className="inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50"
                >
                  <SafeIcon icon={savingOverrideKey === draftKey ? FiIcons.FiLoader : FiIcons.FiSave} className={`mr-2 h-4 w-4 ${savingOverrideKey === draftKey ? 'animate-spin' : ''}`} />
                  Save Name
                </button>
              </div>
            </div>
          );
        })}

        {visibleFieldKeys.map((fieldKey) => {
          const sourceValue = getOriginalFieldValue(item, fieldKey);
          const override = overridesByField[fieldKey];
          const draftKey = `${item.id}:${fieldKey}`;
          const draftValue = overrideDrafts[draftKey] ?? editableValueFor(fieldKey, override?.approved_value_json ?? sourceValue);
          const isJsonValue = Array.isArray(sourceValue) || (sourceValue && typeof sourceValue === 'object');
          const isPersonList = PERSON_LIST_OVERRIDE_FIELDS.has(fieldKey);
          const isTextList = TEXT_LIST_OVERRIDE_FIELDS.has(fieldKey);

          return (
            <div key={fieldKey} className="bg-white rounded-md border border-emerald-100 p-3">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{fieldKey}</p>
                  <p className="mt-1 text-xs text-slate-500 whitespace-pre-line">Current: {stringifyValue(sourceValue)}</p>
                  {override?.approved_at && (
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                      Approved {format(new Date(override.approved_at), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onUseCurrentValue(item, fieldKey)}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-md text-emerald-700 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100"
                >
                  Use Current
                </button>
              </div>
              <div className="mt-3 flex flex-col gap-3">
                {isPersonList ? (
                  <PersonListOverrideInput
                    fieldKey={fieldKey}
                    rows={draftValue}
                    onChange={(nextRows) => onDraftChange(draftKey, nextRows)}
                  />
                ) : (
                  <textarea
                    rows={isJsonValue && !isTextList ? 5 : 3}
                    value={draftValue}
                    onChange={(event) => onDraftChange(draftKey, event.target.value)}
                    className={`block w-full sm:text-sm border-emerald-200 rounded-md py-2 px-3 border focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white ${isJsonValue && !isTextList ? 'font-mono' : ''}`}
                    placeholder={isTextList ? 'Enter one item per line...' : isJsonValue ? 'Enter valid JSON for this value...' : 'Enter the approved drafting value...'}
                  />
                )}
                <button
                  type="button"
                  onClick={() => onSaveOverride(item, fieldKey)}
                  disabled={savingOverrideKey === draftKey}
                  className="inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50"
                >
                  <SafeIcon icon={savingOverrideKey === draftKey ? FiIcons.FiLoader : FiIcons.FiSave} className={`mr-2 h-4 w-4 ${savingOverrideKey === draftKey ? 'animate-spin' : ''}`} />
                  Save
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AttorneyReviewWorkspace = () => {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [matter, setMatter] = useState(null);
  const [review, setReview] = useState(null);
  const [items, setItems] = useState([]);
  const [informationRequests, setInformationRequests] = useState([]);
  const [fieldOverrides, setFieldOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingItemId, setSavingItemId] = useState(null);
  const [requestDrafts, setRequestDrafts] = useState({});
  const [savingRequestKey, setSavingRequestKey] = useState(null);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [savingOverrideKey, setSavingOverrideKey] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadReview = useCallback(async () => {
    if (!profile?.law_firm_id || !matterId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: matterData, error: matterError } = await supabase
        .from('matters')
        .select('*, client:clients (*)')
        .eq('id', matterId)
        .eq('law_firm_id', profile.law_firm_id)
        .single();

      if (matterError) throw matterError;
      setMatter(matterData);

      const { data: reviewData, error: reviewError } = await supabase
        .from('attorney_reviews')
        .select('*')
        .eq('matter_id', matterId)
        .maybeSingle();

      if (reviewError) throw reviewError;
      setReview(reviewData || null);

      if (reviewData?.id) {
        const [
          { data: itemData, error: itemsError },
          { data: requestData, error: requestsError },
          { data: overrideData, error: overridesError }
        ] = await Promise.all([
          supabase
            .from('attorney_review_items')
            .select('*')
            .eq('attorney_review_id', reviewData.id)
            .order('sort_order', { ascending: true })
            .order('created_at', { ascending: true }),
          supabase
            .from('matter_information_requests')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: true }),
          supabase
            .from('matter_field_overrides')
            .select('*')
            .eq('matter_id', matterId)
            .order('updated_at', { ascending: false })
        ]);

        if (itemsError) throw itemsError;
        if (requestsError) throw requestsError;
        if (overridesError) throw overridesError;
        setItems(itemData || []);
        setInformationRequests(requestData || []);
        setFieldOverrides(overrideData || []);
      } else {
        setItems([]);
        setInformationRequests([]);
        setFieldOverrides([]);
      }
    } catch (err) {
      console.error('Attorney review load error:', err);
      setError(formatSupabaseError(err, 'Unable to load attorney review.'));
    } finally {
      setLoading(false);
    }
  }, [matterId, profile?.law_firm_id]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const syncChecklist = async () => {
    if (!matter || syncing) return;

    try {
      setSyncing(true);
      setError(null);
      setNotice(null);

      const { data: reviewId, error: beginError } = await supabase.rpc('begin_attorney_review', {
        p_matter_id: matter.id
      });

      if (beginError) throw beginError;

      const [{ data: reviewData, error: reviewError }, { data: snapshotData, error: snapshotError }, { data: decisionData, error: decisionError }, { data: existingItems, error: existingError }] = await Promise.all([
        supabase.from('attorney_reviews').select('*').eq('id', reviewId).single(),
        supabase
          .from('matter_intake_snapshots')
          .select('*')
          .eq('matter_id', matter.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('estate_plan_decisions')
          .select('*')
          .eq('matter_id', matter.id)
          .maybeSingle(),
        supabase
          .from('attorney_review_items')
          .select('*')
          .eq('attorney_review_id', reviewId)
      ]);

      if (reviewError) throw reviewError;
      if (snapshotError) throw snapshotError;
      if (decisionError) throw decisionError;
      if (existingError) throw existingError;

      const existingByKey = new Map((existingItems || []).map((item) => [item.item_key, item]));
      const generatedItems = buildAttorneyReviewItems({
        matter,
        client: Array.isArray(matter.client) ? matter.client[0] : matter.client,
        intakeSnapshot: snapshotData,
        estatePlanDecisions: decisionData
      }).map((item) => {
        const existing = existingByKey.get(item.item_key);
        return {
          ...item,
          attorney_review_id: reviewId,
          status: existing?.status || 'not_reviewed',
          attorney_decision_json: existing?.attorney_decision_json || {},
          notes: existing?.notes || null,
          completed_by: existing?.completed_by || null,
          completed_at: existing?.completed_at || null
        };
      });

      if (generatedItems.length > 0) {
        const { error: upsertError } = await supabase
          .from('attorney_review_items')
          .upsert(generatedItems, { onConflict: 'attorney_review_id,item_key' });

        if (upsertError) throw upsertError;
      }

      setReview(reviewData);
      setNotice(`Checklist ready with ${generatedItems.length} review items.`);
      await loadReview();
    } catch (err) {
      console.error('Attorney checklist sync error:', err);
      setError(formatSupabaseError(err, 'Unable to prepare attorney review checklist.'));
    } finally {
      setSyncing(false);
    }
  };

  const updateItem = async (item, patch) => {
    try {
      setSavingItemId(item.id);
      setError(null);

      const nextStatus = patch.status ?? item.status;
      const completed = ['reviewed', 'decision_made', 'not_applicable'].includes(nextStatus);
      const updatePayload = {
        ...patch,
        completed_by: completed ? profile.id : null,
        completed_at: completed ? new Date().toISOString() : null
      };

      const { data, error: updateError } = await supabase
        .from('attorney_review_items')
        .update(updatePayload)
        .eq('id', item.id)
        .select('*')
        .single();

      if (updateError) throw updateError;

      setItems((current) => current.map((entry) => (entry.id === data.id ? data : entry)));
    } catch (err) {
      console.error('Attorney review item update error:', err);
      setError(formatSupabaseError(err, 'Unable to update review item.'));
    } finally {
      setSavingItemId(null);
    }
  };

  const useCurrentOverrideValue = (item, fieldKey) => {
    const draftKey = `${item.id}:${fieldKey}`;
    setOverrideDrafts((current) => ({
      ...current,
      [draftKey]: editableValueFor(fieldKey, getOriginalFieldValue(item, fieldKey))
    }));
  };

  const useCurrentNameOverrideValue = (item, group) => {
    const draftKey = `${item.id}:${group.key}_name_parts`;
    setOverrideDrafts((current) => ({
      ...current,
      [draftKey]: nameDraftFor(item, {}, group)
    }));
  };

  const saveFieldOverride = async (item, fieldKey) => {
    if (!matter || !review?.id || savingOverrideKey) return;

    const draftKey = `${item.id}:${fieldKey}`;
    const sourceValue = getOriginalFieldValue(item, fieldKey);
    const draftValue = overrideDrafts[draftKey] ?? editableValueFor(fieldKey, fieldOverrides.find((override) => override.field_key === fieldKey)?.approved_value_json ?? sourceValue);

    try {
      setSavingOverrideKey(draftKey);
      setError(null);
      setNotice(null);

      const approvedValue = parseEditableValue(fieldKey, draftValue, sourceValue);

      const { data: savedOverride, error: overrideError } = await supabase
        .from('matter_field_overrides')
        .upsert({
          matter_id: matter.id,
          law_firm_id: matter.law_firm_id,
          attorney_review_id: review.id,
          review_item_id: item.id,
          field_key: fieldKey,
          value_type: valueTypeFor(approvedValue),
          source_value_json: jsonbValueForSave(sourceValue),
          approved_value_json: jsonbValueForSave(approvedValue),
          approved_by: profile.id,
          approved_at: new Date().toISOString()
        }, { onConflict: 'matter_id,field_key' })
        .select('*')
        .single();

      if (overrideError) throw overrideError;

      setFieldOverrides((current) => {
        const withoutField = current.filter((override) => override.field_key !== fieldKey);
        return [savedOverride, ...withoutField];
      });

      if (!['reviewed', 'decision_made', 'not_applicable'].includes(item.status)) {
        await updateItem(item, { status: 'decision_made' });
      }

      setNotice(`Approved value saved for ${fieldKey}.`);
    } catch (err) {
      console.error('Save field override error:', err);
      setError(err instanceof SyntaxError ? `Approved value for ${fieldKey} must be valid JSON.` : formatSupabaseError(err, 'Unable to save approved value.'));
    } finally {
      setSavingOverrideKey(null);
    }
  };

  const saveNameOverride = async (item, group) => {
    if (!matter || !review?.id || savingOverrideKey) return;

    const draftKey = `${item.id}:${group.key}_name_parts`;
    const draftValue = overrideDrafts[draftKey] ?? nameDraftFor(item, overridesByField, group);
    const normalizedName = {
      first_name: cleanText(draftValue.first_name),
      middle_initial: normalizeMiddleInitial(draftValue.middle_initial),
      last_name: cleanText(draftValue.last_name)
    };
    const fullName = composeName(normalizedName);
    const savedAt = new Date().toISOString();
    const overrideRows = [
      {
        fieldKey: group.firstField,
        value: normalizedName.first_name,
        sourceValue: getOriginalFieldValue(item, group.firstField)
      },
      {
        fieldKey: group.middleField,
        value: normalizedName.middle_initial,
        sourceValue: getOriginalFieldValue(item, group.middleField)
      },
      {
        fieldKey: group.lastField,
        value: normalizedName.last_name,
        sourceValue: getOriginalFieldValue(item, group.lastField)
      },
      {
        fieldKey: group.fullNameField,
        value: fullName,
        sourceValue: getOriginalFieldValue(item, group.fullNameField)
      }
    ];

    try {
      setSavingOverrideKey(draftKey);
      setError(null);
      setNotice(null);

      const { data: savedOverrides, error: overrideError } = await supabase
        .from('matter_field_overrides')
        .upsert(overrideRows.map((override) => ({
          matter_id: matter.id,
          law_firm_id: matter.law_firm_id,
          attorney_review_id: review.id,
          review_item_id: item.id,
          field_key: override.fieldKey,
          value_type: valueTypeFor(override.value),
          source_value_json: jsonbValueForSave(override.sourceValue),
          approved_value_json: jsonbValueForSave(override.value),
          approved_by: profile.id,
          approved_at: savedAt
        })), { onConflict: 'matter_id,field_key' })
        .select('*');

      if (overrideError) throw overrideError;

      const savedFieldKeys = new Set((savedOverrides || []).map((override) => override.field_key));
      setFieldOverrides((current) => [
        ...(savedOverrides || []),
        ...current.filter((override) => !savedFieldKeys.has(override.field_key))
      ]);

      if (!['reviewed', 'decision_made', 'not_applicable'].includes(item.status)) {
        await updateItem(item, { status: 'decision_made' });
      }

      setNotice(`Approved ${group.label.toLowerCase()} saved.`);
    } catch (err) {
      console.error('Save name override error:', err);
      setError(formatSupabaseError(err, `Unable to save approved ${group.label.toLowerCase()}.`));
    } finally {
      setSavingOverrideKey(null);
    }
  };

  const createInformationRequest = async (item) => {
    const question = requestDrafts[item.id]?.trim();
    if (!question || savingRequestKey) return;

    try {
      setSavingRequestKey(item.id);
      setError(null);
      setNotice(null);

      const { error: insertError } = await supabase
        .from('matter_information_requests')
        .insert({
          matter_id: matter.id,
          law_firm_id: matter.law_firm_id,
          attorney_review_id: review.id,
          review_item_id: item.id,
          requested_from_role: 'advisor',
          question,
          blocks_review_completion: true,
          created_by: profile.id
        });

      if (insertError) throw insertError;

      await Promise.all([
        supabase
          .from('attorney_review_items')
          .update({ status: 'waiting_for_information' })
          .eq('id', item.id),
        supabase
          .from('attorney_reviews')
          .update({ status: 'waiting_for_information' })
          .eq('id', review.id),
        supabase
          .from('matters')
          .update({ status: 'more_information_needed' })
          .eq('id', matter.id)
          .eq('law_firm_id', profile.law_firm_id)
      ]);

      setRequestDrafts((current) => ({ ...current, [item.id]: '' }));
      setNotice('Information request sent to advisor.');
      await loadReview();
    } catch (err) {
      console.error('Create information request error:', err);
      setError(formatSupabaseError(err, 'Unable to send information request.'));
    } finally {
      setSavingRequestKey(null);
    }
  };

  const resolveInformationRequest = async (request) => {
    if (savingRequestKey) return;

    try {
      setSavingRequestKey(request.id);
      setError(null);
      setNotice(null);

      const { error: updateError } = await supabase
        .from('matter_information_requests')
        .update({
          status: 'resolved',
          resolved_by: profile.id,
          resolved_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      const { count, error: countError } = await supabase
        .from('matter_information_requests')
        .select('*', { count: 'exact', head: true })
        .eq('matter_id', matter.id)
        .eq('blocks_review_completion', true)
        .in('status', ['open', 'answered']);

      if (countError) throw countError;

      if ((count || 0) === 0 && review?.status === 'waiting_for_information') {
        await Promise.all([
          supabase
            .from('attorney_reviews')
            .update({ status: 'in_progress' })
            .eq('id', review.id),
          supabase
            .from('matters')
            .update({ status: 'attorney_review' })
            .eq('id', matter.id)
            .eq('law_firm_id', profile.law_firm_id)
        ]);
      }

      setNotice('Information request resolved.');
      await loadReview();
    } catch (err) {
      console.error('Resolve information request error:', err);
      setError(formatSupabaseError(err, 'Unable to resolve information request.'));
    } finally {
      setSavingRequestKey(null);
    }
  };

  const completeReview = async () => {
    if (!matter || completing) return;

    try {
      setCompleting(true);
      setError(null);
      setNotice(null);

      const { error: completeError } = await supabase.rpc('complete_attorney_review', {
        p_matter_id: matter.id
      });

      if (completeError) throw completeError;

      setNotice('Attorney review marked complete.');
      await loadReview();
    } catch (err) {
      console.error('Complete attorney review error:', err);
      setError(formatSupabaseError(err, 'Unable to complete attorney review.'));
    } finally {
      setCompleting(false);
    }
  };

  const markReadyForDocumentGeneration = async () => {
    if (!matter || markingReady) return;

    try {
      setMarkingReady(true);
      setError(null);
      setNotice(null);

      const { error: readyError } = await supabase.rpc('mark_ready_for_document_generation', {
        p_matter_id: matter.id
      });

      if (readyError) throw readyError;

      setNotice('Plan marked ready for document generation.');
      await loadReview();
    } catch (err) {
      console.error('Ready for document generation error:', err);
      setError(formatSupabaseError(err, 'Unable to mark plan ready for document generation.'));
    } finally {
      setMarkingReady(false);
    }
  };

  const groupedItems = useMemo(() => {
    return items.reduce((groups, item) => {
      const category = item.category || 'General';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
      return groups;
    }, {});
  }, [items]);

  const overridesByField = useMemo(() => {
    return fieldOverrides.reduce((lookup, override) => {
      lookup[override.field_key] = override;
      return lookup;
    }, {});
  }, [fieldOverrides]);

  const completedCount = items.filter((item) => ['reviewed', 'decision_made', 'not_applicable'].includes(item.status)).length;
  const requiredOpenItems = items.filter((item) => item.is_required && !['reviewed', 'decision_made', 'not_applicable'].includes(item.status));
  const blockingInformationRequests = informationRequests.filter((request) => request.blocks_review_completion && ['open', 'answered'].includes(request.status));
  const requiredOpenCount = requiredOpenItems.length;
  const blockingRequestCount = blockingInformationRequests.length;
  const isAttorney = profile?.role === 'attorney';
  const reviewIsComplete = review?.status === 'complete' || review?.status === 'ready_for_document_generation';
  const reviewIsReadyForDocs = review?.status === 'ready_for_document_generation' || matter?.status === 'ready_for_document_generation';
  const canComplete = isAttorney && review?.id && !reviewIsComplete;
  const completionBlocked = requiredOpenCount > 0 || blockingRequestCount > 0;
  const completionHelpText = !review?.id
    ? 'Begin review before completing it.'
    : !isAttorney
      ? 'Only attorneys can complete attorney review.'
      : completionBlocked
        ? 'Resolve the blocking items below before completing review.'
        : reviewIsComplete
          ? 'Attorney review is already complete.'
          : 'All required review items are clear.';

  if (!profile?.law_firm_id) {
    return (
      <div className="p-8 text-center bg-white rounded-lg border border-slate-200">
        Law firm access requires a law firm profile assignment.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading attorney review...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/law-firm/matters/${matterId}`)}
            className="mr-4 p-2 rounded-full hover:bg-white transition-colors text-slate-400 hover:text-slate-600"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Attorney Review</h1>
            <p className="text-sm text-slate-500 mt-1">
              {matter?.client?.first_name} {matter?.client?.last_name} - {matter?.plan_type || 'Estate plan'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={syncChecklist}
            disabled={syncing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            <SafeIcon icon={syncing ? FiIcons.FiLoader : FiIcons.FiRefreshCw} className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {items.length > 0 ? 'Refresh Checklist' : 'Begin Review'}
          </button>
          {!reviewIsComplete && (
            <button
              onClick={completeReview}
              disabled={!canComplete || completing || completionBlocked}
              title={completionHelpText}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50"
            >
              <SafeIcon icon={completing ? FiIcons.FiLoader : FiIcons.FiCheckCircle} className={`mr-2 h-4 w-4 ${completing ? 'animate-spin' : ''}`} />
              Complete Review
            </button>
          )}
          {review?.status === 'complete' && isAttorney && (
            <button
              onClick={markReadyForDocumentGeneration}
              disabled={markingReady}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50"
            >
              <SafeIcon icon={markingReady ? FiIcons.FiLoader : FiIcons.FiFileText} className={`mr-2 h-4 w-4 ${markingReady ? 'animate-spin' : ''}`} />
              Ready for Documents
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {notice && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md text-sm text-green-700">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Review Status</p>
          <p className="mt-2 text-lg font-bold text-slate-900 capitalize">{review?.status?.replaceAll('_', ' ') || 'Not Started'}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Progress</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{completedCount} / {items.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Required Open</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{requiredOpenCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Started</p>
          <p className="mt-2 text-lg font-bold text-slate-900">
            {review?.started_at ? format(new Date(review.started_at), 'MMM d, yyyy') : 'Not started'}
          </p>
        </div>
      </div>

      <ProfessionalLearningInsights matter={matter} />

      <ProfessionalAssignLearning matter={matter} profile={profile} />

      {items.length > 0 && !reviewIsComplete && (
        <div className={`rounded-xl border p-5 ${completionBlocked ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={completionBlocked ? FiIcons.FiAlertTriangle : FiIcons.FiCheckCircle} className={`h-5 w-5 ${completionBlocked ? 'text-amber-600' : 'text-green-600'}`} />
                <h2 className={`text-sm font-bold uppercase tracking-tight ${completionBlocked ? 'text-amber-900' : 'text-green-900'}`}>
                  Completion Check
                </h2>
              </div>
              <p className={`mt-2 text-sm ${completionBlocked ? 'text-amber-800' : 'text-green-800'}`}>
                {completionHelpText}
              </p>
            </div>
            {!isAttorney && (
              <span className="inline-flex px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-full bg-white text-slate-600 border border-slate-200">
                Attorney Only
              </span>
            )}
          </div>

          {completionBlocked && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {requiredOpenItems.length > 0 && (
                <div className="bg-white rounded-lg border border-amber-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-3">Required Items Still Open</p>
                  <ul className="space-y-2">
                    {requiredOpenItems.map((item) => (
                      <li key={item.id} className="text-sm text-slate-700">
                        <span className="font-semibold">{item.title}</span>
                        <span className="text-slate-400"> - {getStatusLabel(item.status)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {blockingInformationRequests.length > 0 && (
                <div className="bg-white rounded-lg border border-amber-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-3">Blocking Information Requests</p>
                  <ul className="space-y-2">
                    {blockingInformationRequests.map((request) => (
                      <li key={request.id} className="text-sm text-slate-700">
                        <span className="font-semibold">{request.question}</span>
                        <span className="text-slate-400"> - {request.status.replaceAll('_', ' ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {reviewIsComplete && (
        <div className={`rounded-xl border p-5 ${reviewIsReadyForDocs ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiIcons.FiCheckCircle} className={`h-5 w-5 mt-0.5 ${reviewIsReadyForDocs ? 'text-emerald-700' : 'text-green-700'}`} />
            <div>
              <h2 className={`text-sm font-bold uppercase tracking-tight ${reviewIsReadyForDocs ? 'text-emerald-900' : 'text-green-900'}`}>
                {reviewIsReadyForDocs ? 'Ready for Document Generation' : 'Attorney Review Complete'}
              </h2>
              <p className={`mt-2 text-sm ${reviewIsReadyForDocs ? 'text-emerald-800' : 'text-green-800'}`}>
                {reviewIsReadyForDocs
                  ? 'This plan has been reviewed and marked ready for document generation.'
                  : 'The attorney review is complete. An attorney can now mark the plan ready for document generation when appropriate.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <SafeIcon icon={FiIcons.FiClipboard} className="h-12 w-12 text-slate-200 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900">No checklist yet</h2>
          <p className="text-sm text-slate-500 mt-2">Begin review to create the first attorney checklist for this plan.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, categoryItems]) => (
            <section key={category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">{category}</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {categoryItems.map((item) => (
                  <article key={item.id} className="p-6 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <SafeIcon icon={item.warning_level === 'info' ? FiIcons.FiInfo : FiIcons.FiAlertTriangle} className={`h-4 w-4 ${warningStyles[item.warning_level] || warningStyles.info}`} />
                          <h3 className="text-base font-bold text-slate-900">{item.title}</h3>
                          {item.is_required && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-red-50 text-red-700 border border-red-100">
                              Required
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border ${statusStyles[item.status] || statusStyles.not_reviewed}`}>
                            {getStatusLabel(item.status)}
                          </span>
                        </div>
                        {item.description && (
                          <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                        )}
                      </div>
                      <select
                        value={item.status}
                        disabled={savingItemId === item.id}
                        onChange={(event) => updateItem(item, { status: event.target.value })}
                        className="block w-full lg:w-56 sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white disabled:bg-slate-50"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <AnswerPreview answers={item.original_answer_json} />

                    <ApprovedOverrideEditor
                      item={item}
                      overridesByField={overridesByField}
                      overrideDrafts={overrideDrafts}
                      savingOverrideKey={savingOverrideKey}
                      onDraftChange={(draftKey, value) => setOverrideDrafts((current) => ({ ...current, [draftKey]: value }))}
                      onUseCurrentValue={useCurrentOverrideValue}
                      onUseNameCurrentValue={useCurrentNameOverrideValue}
                      onSaveNameOverride={saveNameOverride}
                      onSaveOverride={saveFieldOverride}
                    />

                    <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-bold text-blue-950 flex items-center">
                            <SafeIcon icon={FiIcons.FiMessageSquare} className="mr-2 h-4 w-4 text-blue-600" />
                            Advisor Information Request
                          </h4>
                          <p className="text-xs text-blue-700 mt-1">
                            Ask the advisor for clarification. Open or answered requests block review completion until resolved.
                          </p>
                        </div>
                      </div>

                      {informationRequests.filter((request) => request.review_item_id === item.id).length > 0 && (
                        <div className="mt-4 space-y-3">
                          {informationRequests
                            .filter((request) => request.review_item_id === item.id)
                            .map((request) => (
                              <div key={request.id} className="bg-white border border-blue-100 rounded-md p-3">
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">{request.question}</p>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                                      {request.status.replaceAll('_', ' ')}
                                      {request.created_at && ` - ${format(new Date(request.created_at), 'MMM d, yyyy')}`}
                                    </p>
                                    {request.response && (
                                      <div className="mt-3 bg-slate-50 border border-slate-100 rounded-md p-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Advisor Response</p>
                                        <p className="text-sm text-slate-700 whitespace-pre-line">{request.response}</p>
                                      </div>
                                    )}
                                  </div>
                                  {['open', 'answered'].includes(request.status) && (
                                    <button
                                      onClick={() => resolveInformationRequest(request)}
                                      disabled={savingRequestKey === request.id}
                                      className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-md text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 disabled:opacity-50"
                                    >
                                      <SafeIcon icon={savingRequestKey === request.id ? FiIcons.FiLoader : FiIcons.FiCheck} className={`mr-2 h-3.5 w-3.5 ${savingRequestKey === request.id ? 'animate-spin' : ''}`} />
                                      Resolve
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                      <div className="mt-4 flex flex-col lg:flex-row gap-3">
                        <textarea
                          rows={2}
                          value={requestDrafts[item.id] || ''}
                          onChange={(event) => setRequestDrafts((current) => ({ ...current, [item.id]: event.target.value }))}
                          className="block w-full sm:text-sm border-blue-200 rounded-md py-2 px-3 border focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                          placeholder="Ask the advisor for missing or unclear information..."
                        />
                        <button
                          onClick={() => createInformationRequest(item)}
                          disabled={!requestDrafts[item.id]?.trim() || savingRequestKey === item.id || !review?.id}
                          className="inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50"
                        >
                          <SafeIcon icon={savingRequestKey === item.id ? FiIcons.FiLoader : FiIcons.FiSend} className={`mr-2 h-4 w-4 ${savingRequestKey === item.id ? 'animate-spin' : ''}`} />
                          Send
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Attorney Notes</label>
                      <textarea
                        rows={3}
                        defaultValue={item.notes || ''}
                        onBlur={(event) => {
                          if ((item.notes || '') !== event.target.value) {
                            updateItem(item, { notes: event.target.value.trim() || null });
                          }
                        }}
                        className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                        placeholder="Add review notes for this item..."
                      />
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttorneyReviewWorkspace;
